import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { LambdaClient, InvokeCommand } from '@aws-sdk/client-lambda';

// API URL
const apiGatewayUrl = 'https://m082qhj8m5.execute-api.us-east-1.amazonaws.com/prod';

// AWS Region for Lambda
const REGION = 'us-east-1';

// Create Context for managing client config and prompt master
const MyContext = createContext(null);

// Context provider to handle CRUD operations
export const MyProvider = ({ children }) => {
  const [clientConfig, setClientConfig] = useState(null);
  const [promptMaster, setPromptMaster] = useState(null);
  const [selectedClientCode, setSelectedClientCode] = useState('');
  const [clientCodes, setClientCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [clients, setClients] = useState([]);

  // Fetch the list of clientCodes from the clientmaster API
  const fetchClientCodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiGatewayUrl}/clientmaster`);
      setClientCodes(response.data);
      if (!selectedClientCode) {
        setSelectedClientCode(response.data[0]?.clientcode || '');
      }
    } catch (error) {
      setError('Error fetching client codes.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllClients = async () => {
    try {
      const data = await axios.get(`${apiGatewayUrl}/clientmaster`);
      setClients(data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  // Fetch ClientConfig Data based on clientCode
  const fetchClientConfig = async (clientCode) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiGatewayUrl}/clientconfig?clientcode=${clientCode}`);
      setClientConfig({ ...response.data });
    } catch (error) {
      if (error.response && error.response.status === 404) {
        console.warn(`No client config found for ${clientCode} — initializing empty config.`);
        setClientConfig({ rulesset: { promptcategory: [] } });
      } else {
        console.error('Unexpected error in fetchClientConfig:', error);
        setError('Error fetching client config.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch PromptMaster Data
  const fetchPromptMaster = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiGatewayUrl}/promptmaster`);
      setPromptMaster(response.data);
    } catch (error) {
      setError('Error fetching prompt data.');
    } finally {
      setLoading(false);
    }
  };

  // Add a new ClientConfig category
  const addClientConfig = async (promptcategory, score) => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${apiGatewayUrl}/clientconfig`, {
        clientcode: selectedClientCode,
        promptcategory,
        score,
      });
  //    alert('Prompt added successfully.');
      fetchClientConfig(selectedClientCode);
    } catch (error) {
      setError('Error adding ClientConfig.');
    } finally {
      setLoading(false);
    }
  };

  // Update ClientConfig category
  const updateClientConfig = async (promptcategory, score) => {
    setLoading(true);
    setError(null);
    try {
      await axios.put(`${apiGatewayUrl}/clientconfig`, {
        clientcode: selectedClientCode,
        promptcategory,
        score,
      });
   //   alert('Prompt updated successfully.');
      fetchClientConfig(selectedClientCode);
    } catch (error) {
      setError('Error updating ClientConfig.');
    } finally {
      setLoading(false);
    }
  };

  // Delete ClientConfig category
  const deleteClientConfig = async (promptcategory) => {
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`${apiGatewayUrl}/clientconfig`, {
        data: {
          clientcode: selectedClientCode,
          promptcategory,
        },
      });
   //   alert('Prompt deleted successfully.');
      await fetchClientConfig(selectedClientCode);
    } catch (error) {
      setError('Error deleting ClientConfig.');
    } finally {
      setLoading(false);
    }
  };

  // Add PromptMaster
  const addPromptMaster = async (categoryName, promptName) => {
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${apiGatewayUrl}/promptmaster`, {
        promptcategory: categoryName,
        promptname: promptName,
      });
    //  alert('Prompt category added successfully.');
      fetchPromptMaster();
    } catch (error) {
      setError('Error adding PromptMaster.');
    } finally {
      setLoading(false);
    }
  };

  // Update PromptMaster
  const updatePromptMaster = async (categoryName, promptName) => {
    setLoading(true);
    setError(null);
    try {
      await axios.put(`${apiGatewayUrl}/promptmaster`, {
        promptcategory: categoryName,
        promptname: promptName,
      });
     // alert('Prompt category updated successfully.');
      fetchPromptMaster();
    } catch (error) {
      setError('Error updating PromptMaster.');
    } finally {
      setLoading(false);
    }
  };

  // Delete PromptMaster
  const deletePromptMaster = async (categoryName) => {
    setLoading(true);
    setError(null);
    try {
      await axios.delete(`${apiGatewayUrl}/promptmaster`, {
        data: {
          promptcategory: categoryName,
        },
      });
     // alert('Prompt category deleted successfully.');
      fetchPromptMaster();
    } catch (error) {
      setError('Error deleting PromptMaster.');
    } finally {
      setLoading(false);
    }
  };

  // Invoke Lambda Function: callTranscribeBot
  const invokeTranscribeBot = async (inputText) => {
    const client = new LambdaClient({ region: REGION });

    const params = {
      FunctionName: 'callTranscribeBot',
      InvocationType: 'RequestResponse',
      Payload: JSON.stringify({ inputText }),
    };

    try {
      const command = new InvokeCommand(params);
      const response = await client.send(command);
      const responsePayload = JSON.parse(new TextDecoder("utf-8").decode(response.Payload));
      const parsedBody = JSON.parse(responsePayload.body);
      let outputText = parsedBody.data.results[0].outputText;
      return outputText.replace(/\n/g, '');
    } catch (error) {
      console.error('Error invoking Lambda callTranscribeBot:', error);
      throw new Error('Failed to get response from callTranscribeBot.');
    }
  };

  // Fetch initial data
  useEffect(() => {
    fetchClientCodes();
  }, []);

  // Fetch client config + prompt master when client changes
  useEffect(() => {
    if (selectedClientCode) {
      setClientConfig(null);
      fetchClientConfig(selectedClientCode);
      fetchPromptMaster();
    }
  }, [selectedClientCode]);

  return (
    <MyContext.Provider
      value={{
        clientConfig,
        promptMaster,
        clientCodes,
        selectedClientCode,
        setSelectedClientCode,
        fetchClientConfig,
        fetchPromptMaster,
        fetchClientCodes,
        refreshClientCodes: fetchClientCodes, // ✅ exposed here
        addClientConfig,
        updateClientConfig,
        deleteClientConfig,
        addPromptMaster,
        updatePromptMaster,
        deletePromptMaster,
        invokeTranscribeBot,
        loading,
        error,
      }}
    >
      {children}
    </MyContext.Provider>
  );
};

// Custom hook to use the context
export const useMyContext = () => useContext(MyContext);

